import {
  AHIcon,
  AHText,
  AHTextFonts,
  Box,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Platform,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useDeviceOrientation } from "../../common/Utils/useDeviceOrientation";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { HeaderButton } from "../../components";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";

import { PLUProductItem } from "./PLUProductItem/PLUProductItem";
import { PLUAddProductItem } from "./PLUProductItem/PLUAddProductItem";
import { PLUCategorySelector } from "./PLUCategorySelector";
import { PLUCategoryItem_PLUCategory$key } from "./PLUCategoryItem/__generated__/PLUCategoryItem_PLUCategory.graphql";
import { PLUScreenQuery } from "./__generated__/PLUScreenQuery.graphql";

import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { PLUScreenEmpty, PLUScreenStartSearch } from "./PLUScreenEmpty";
import { PLUScreenOnboarding } from "./PLUScreenOnboarding";

import { useFocusEffect, useIsFocused } from "@react-navigation/native";

import { AHSegmentedControl } from "../../components/mobile/AHSegmentedControl";
import { FlashList, FlashListRef } from "@shopify/flash-list";
import Animated, { useSharedValue } from "react-native-reanimated";
import { collator } from "../../utils";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { PLUScreenUpsell } from "./PLUScreenUpsell";
import { Features, useHasFeatureInTier } from "../../useHasFeatureInTier";
import { useHasPermissions } from "../../OS/Login/usePermissions";
import { useInterval } from "widgets/useInterval";

import { useSearch } from "../../components/mobile/useSearch";
import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";
type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"TeamPLU">;
  route: UserLoggedInNavigatorRouteProp<"TeamPLU">;
};

export function TeamPLUScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerPLUScreen fetchKey={fetchKey} {...props} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerPLUScreen(props: { fetchKey: string } & Props) {
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();

  const theme = useTheme();
  const sizeClass = useSizeClass().width;
  const windowWidth = useWindowDimensions().width;
  const { landscape } = useDeviceOrientation();

  const numColumns = useMemo(() => {
    switch (sizeClass) {
      case SizeClass.COMPACT:
        return 1;

      case SizeClass.REGULAR:
        if (landscape) {
          return 4;
        }
        return 3;
      case SizeClass.WIDE:
        return 6;
    }
  }, [landscape, sizeClass]);

  const itemWidth = useMemo(
    () => windowWidth / numColumns,
    [numColumns, windowWidth]
  );

  const [pollKey, setPollKey] = useState(0);

  const isFocused = useIsFocused();

  const poll = useCallback(() => {
    if (isFocused) {
      setPollKey((pk) => pk + 1);
    }
  }, [isFocused]);
  useInterval(poll, 60000 * 5);
  const data = useLazyLoadQuery<PLUScreenQuery>(
    graphql`
      query PLUScreenQuery($teamId: ID!, $includeHidden: Boolean) {
        store {
          ...usePermissions_store
          ...useHasFeatureInTier_Store
          team(id: $teamId) {
            allCategories: pluCategories(includeHidden: true) {
              products {
                title
                id
                active
                eanCode
                pluCode
                ...PLUProductItem_PLUProduct
              }
            }
            pluCategories(includeHidden: $includeHidden) {
              id
              title
              color
              ...PLUCategoryItem_PLUCategory
              products {
                id
                title
                active
                eanCode
                pluCode
                ...PLUProductItem_PLUProduct
              }
            }
          }
        }
      }
    `,
    {
      teamId: props.route.params.teamId,

      includeHidden: props.route.params.isShowingAllCategories,
    },
    {
      fetchKey: props.fetchKey + pollKey,
      fetchPolicy: "store-and-network",
    }
  );

  const canEdit = useHasPermissions(data.store, "PLU_ADMIN");

  const hasFeatureInTier = useHasFeatureInTier(
    Features.CHECKOUT_MODE,
    data.store
  );

  const emtpySearch = props.route.params.searchText.length > 0;
  const products = useMemo(() => {
    if (props.route.params.selectedCategoryId === undefined || emtpySearch) {
      return (
        data.store.team?.pluCategories
          .flatMap((category) => category.products)
          .sort((a, b) => collator.compare(a.title, b.title)) ?? []
      );
    }

    return (
      data.store.team?.pluCategories
        .find(
          (category) => category.id === props.route.params.selectedCategoryId
        )
        ?.products.slice()
        .sort((a, b) => collator.compare(a.title, b.title)) ?? []
    );
  }, [
    data.store.team?.pluCategories,
    emtpySearch,
    props.route.params.selectedCategoryId,
  ]);

  const allProducts = useMemo(
    () =>
      data.store.team?.allCategories.flatMap((category) => category.products) ??
      [],
    [data.store.team?.allCategories]
  );

  const [isSearching, searchedProducts] = useSearch(
    props.route.params.searchText,
    allProducts,
    titlePluMapper
  );

  const filteredProducts = useMemo(() => {
    if (props.route.params.searching) {
      if (props.route.params.searchText === "") {
        return [];
      }
      const results = searchedProducts.map(([p]) => p);

      if (props.route.params.mode === "selfCheckoutCheck") {
        return results.filter((p) => p?.eanCode);
      }
      return results.filter((p) => p);
    }
    if (props.route.params.mode === "selfCheckoutCheck") {
      return products.filter((p) => p.eanCode);
    }

    return products;
  }, [
    props.route.params.searching,
    props.route.params.mode,
    props.route.params.searchText,
    products,
    searchedProducts,
  ]);

  const listData = useMemo(() => {
    return props.route.params.isEditing
      ? ["ADD_ITEM" as const, ...filteredProducts]
      : filteredProducts.filter((s) => s.active);
  }, [filteredProducts, props.route.params.isEditing]);

  const focusedProductId = useSharedValue<string | null>(null);

  useEffect(() => {
    focusedProductId.value = null;
  }, [filteredProducts, focusedProductId]);

  const timeout = useRef<undefined | ReturnType<typeof setTimeout>>(undefined);
  const onFocus = useCallback(
    (id: string) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      if (focusedProductId.value) {
        focusedProductId.value = null;
      } else {
        focusedProductId.value = id;
        timeout.current = setTimeout(() => {
          focusedProductId.value = null;
        }, 12_000);
      }
    },
    [focusedProductId]
  );
  const renderItem = useCallback(
    ({ item }: { item: (typeof listData)[number] }) =>
      item === "ADD_ITEM" ? (
        <PLUAddProductItem
          onPress={() => {
            const categoryId =
              props.route.params.selectedCategoryId !== undefined
                ? props.route.params.selectedCategoryId
                : data.store.team?.pluCategories?.length === 1
                  ? data.store.team.pluCategories[0].id
                  : undefined;
            props.navigation.navigate("PLUProductCreate", {
              categoryId,
            });
          }}
          itemWidth={itemWidth}
        />
      ) : (
        <PLUProductItem
          focusedFocusId={focusedProductId}
          pluProduct={item}
          itemWidth={itemWidth}
          onFocus={onFocus}
        />
      ),
    [
      itemWidth,
      focusedProductId,
      onFocus,
      props.route.params.selectedCategoryId,
      props.navigation,
      data.store.team?.pluCategories,
    ]
  );

  const sortedCategories = useMemo(() => {
    return (
      data.store.team?.pluCategories
        .slice()
        .sort((a, b) => collator.compare(a.title, b.title)) ?? []
    );
  }, [data.store.team?.pluCategories]);

  const pluCategories: (
    | "ADD_ITEM"
    | (PLUCategoryItem_PLUCategory$key & { id: string })
  )[] = useMemo(() => {
    return props.route.params.isEditing
      ? ["ADD_ITEM" as const, ...sortedCategories]
      : sortedCategories;
  }, [props.route.params.isEditing, sortedCategories]);

  const ref = useRef<FlashListRef<any>>(null);

  useLayoutEffect(() => {
    if (Platform.OS === "ios") {
      //   ref.current?.prepareForLayoutAnimationRender();
      //     LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
  }, [props.route.params.isEditing, props.route.params.searching]);

  const selectedCategory = useMemo(
    () =>
      props.route.params.selectedCategoryId
        ? data.store.team?.pluCategories.find(
            (c) => c.id === props.route.params.selectedCategoryId
          )
        : undefined,
    [data.store.team?.pluCategories, props.route.params.selectedCategoryId]
  );

  if (!hasFeatureInTier) {
    return <PLUScreenUpsell />;
  }

  if ((data.store.team?.pluCategories?.length ?? 0) > 0) {
    const showCategoryBar = !props.route.params.searching;

    // fix bug where switching from non sticky header to sticky header crashes on android

    return (
      <>
        <Box style={{ height: 95 }} spaceHorizontal="small">
          <Box
            style={{
              flexDirection: "row",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box style={{ flexDirection: "row", alignItems: "center" }}>
              {canEdit ? (
                <HeaderButton
                  type={props.route.params.isEditing ? "DONE" : "EDIT"}
                  onPress={() => {
                    props.navigation.setParams({
                      isEditing: !props.route.params.isEditing,
                      mode: "checkout",
                    });
                  }}
                />
              ) : null}
            </Box>

            <AHSegmentedControl
              appearance="light"
              selectedIndex={props.route.params.mode === "checkout" ? 0 : 1}
              values={["Kassa", "Avstämning"]}
              onChange={(e) => {
                props.navigation.setParams({
                  teamId: props.route.params.teamId,
                  mode:
                    e.nativeEvent.selectedSegmentIndex === 0
                      ? "checkout"
                      : "selfCheckoutCheck",
                });
              }}
              style={{
                width: 200,
              }}
            />
          </Box>
          <SearchBar
            searchText={props.route.params.searchText}
            setSearchText={(searchText) => {
              props.navigation.setParams({ searching: true, searchText });
            }}
            onFocus={() => {
              props.navigation.setParams({ searching: true });
            }}
            onDone={() => {
              props.navigation.setParams({ searching: false });
            }}
            isSearching={props.route.params.searching}
          />
        </Box>

        {showCategoryBar ? (
          <PLUCategorySelector
            pluCategories={pluCategories}
            selectedCategory={props.route.params.selectedCategoryId}
            setSelectedCategory={(selectedCategoryId) =>
              props.navigation.setParams({ selectedCategoryId })
            }
          />
        ) : null}

        <FlashList
          ref={ref}
          contentContainerStyle={{
            paddingBottom: insets.bottom + layout.space.medium,
            backgroundColor: theme.background,
          }}
          ListEmptyComponent={
            props.route.params.searching &&
            !isSearching &&
            props.route.params.searchText === "" ? (
              <PLUScreenStartSearch />
            ) : (
              <PLUScreenEmpty
                searching={props.route.params.searching}
                searchText={props.route.params.searchText}
                selectedCategory={selectedCategory}
              />
            )
          }
          contentInsetAdjustmentBehavior="always"
          data={listData}
          keyExtractor={(item) => (item === "ADD_ITEM" ? "ADD_ITEM" : item.id)}
          renderItem={renderItem}
          numColumns={numColumns}
          renderScrollComponent={KeyboardAwareFlashListScrollComponent}
          automaticallyAdjustKeyboardInsets
        />
      </>
    );
  }

  return <PLUScreenOnboarding />;
}

const AnimatedTouchable = Animated.createAnimatedComponent(TouchableOpacity);

const SearchBar = ({
  searchText,
  setSearchText,
  isSearching,
  onDone,
  onFocus,
}: {
  searchText?: string;
  setSearchText: (text: string) => void;
  isSearching: boolean;
  onFocus: () => void;
  onDone: () => void;
}) => {
  const inputRef = useRef<TextInput>(null);

  const intl_token = useIntlTokens();
  useFocusEffect(
    React.useCallback(() => {
      return () => {
        inputRef.current?.blur();
      };
    }, [inputRef])
  );

  const clear = () => {
    setSearchText("");
  };

  return (
    <Box style={[styles.wrapper]}>
      <Box color="surface" style={[styles.container]}>
        <Box
          style={{ width: 30, alignItems: "center", justifyContent: "center" }}
        >
          <AHIcon type="search" />
        </Box>
        <TextInput
          ref={inputRef as any}
          placeholderTextColor="rgb(162,162,167)"
          value={searchText}
          onChangeText={setSearchText}
          style={styles.textInput}
          returnKeyType="search"
          onFocus={onFocus}
          placeholder={intl_token.SearchProducts}
        />
        {searchText !== "" ? (
          <AnimatedTouchable
            style={{
              alignItems: "flex-end",
              width: isSearching ? 60 : 0,
              position: "absolute",
              right: 10,
            }}
            hitSlop={{ left: 30, top: 15, bottom: 20, right: 0 }}
            onPress={() => {
              clear();
            }}
          >
            <AHIcon type="clear" />
          </AnimatedTouchable>
        ) : null}
      </Box>

      <AnimatedTouchable
        style={{
          alignItems: "center",
          width: isSearching ? 60 : 0,
        }}
        hitSlop={{
          top: 14,
          bottom: 20,
          right: 10,
        }}
        onPress={() => {
          clear();
          onDone();

          inputRef.current?.blur();
        }}
      >
        <AHText
          color="primary"
          style={{
            fontSize: 16,
            maxHeight: 20,
          }}
        >
          {isSearching ? intl_token.Done : ""}
        </AHText>
      </AnimatedTouchable>
    </Box>
  );
};

function titlePluMapper<
  T extends { title?: string | null; pluCode?: string | null },
>(item: T) {
  return [item.title, item.pluCode].filter((a) => a).join(" ");
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    flex: 1,
    height: 40,
    borderRadius: 20,
  },
  textInput: {
    flex: 1,
    fontFamily: AHTextFonts.REGULAR,
    fontSize: 15,
  },
});
